import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { trackEvent } from "@/lib/track";
import UserSimulationsList from "@/components/UserSimulationsList";
import SalesQuoteButton from "@/components/SalesQuoteButton";
import DeleteAccountButton from "@/components/DeleteAccountButton";
import VisionAiOptInBanner from "@/components/VisionAiOptInBanner";
import api from "@/lib/api";
import { Home as HomeIcon, Building2, ArrowRight, Sparkles, BookOpen } from "lucide-react";

const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3Njk5ODAxfDA&ixlib=rb-4.1.0&q=85";
const EXTERIOR_IMG =
  "https://images.unsplash.com/photo-1757439402296-000be181e38b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvciUyMG5pZ2h0fGVufDB8fHx8MTc3NzY5OTgwMXww&ixlib=rb-4.1.0&q=85";

export default function DashboardSelector() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api
      .get("/simulations/usage/today")
      .then((r) => setUsage(r.data))
      .catch(() => {});
  }, []);

  const Card = ({ to, label, num, img, icon, description, testId }) => (
    <Link
      to={to}
      data-testid={testId}
      className="relative group h-[360px] md:h-[560px] overflow-hidden rounded-lg border border-white/10 hover:border-orange-500 transition-all duration-500 cursor-pointer"
    >
      <img
        src={img}
        alt={label}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20 group-hover:from-black/95 group-hover:via-black/30 transition-all duration-500" />
      <div className="absolute inset-x-0 top-0 p-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {icon}
          <span className="text-xs uppercase tracking-[0.3em] text-orange-500">
            {t("nav.dashboard")}
          </span>
        </div>
        <div className="font-cormorant text-zinc-500 group-hover:text-orange-500/70 transition-colors text-2xl">
          {num}
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
        <h2 className="font-outfit font-black text-white text-5xl md:text-7xl tracking-tighter leading-none">
          {label}
        </h2>
        <p className="text-zinc-300 mt-4 max-w-md text-base md:text-lg">
          {description}
        </p>
        <div className="mt-8 inline-flex items-center gap-2 text-orange-400 group-hover:text-orange-300 transition">
          <span className="text-sm uppercase tracking-[0.3em]">{t("dashboard.select_cta")}</span>
          <ArrowRight
            size={20}
            strokeWidth={1.5}
            className="group-hover:translate-x-2 transition-transform"
          />
        </div>
      </div>
      {/* glowing border on hover */}
      <div className="absolute inset-0 ring-0 group-hover:ring-2 group-hover:ring-orange-500/40 rounded-lg pointer-events-none transition-all duration-500" />
    </Link>
  );

  return (
    <div className="min-h-screen bg-background grain" data-testid="dashboard-selector-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3 flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.5} />
              {t("nav.dashboard")}
            </div>
            <h1 className="font-outfit font-black text-white text-5xl md:text-7xl tracking-tighter leading-[0.95]">
              {t("dashboard.select_title")}
              <br />
              <span className="font-light italic font-cormorant text-zinc-400">
                {t("dashboard.select_subtitle")}
              </span>
            </h1>
          </div>

          {usage && (
            <div
              className="border border-white/10 rounded-lg px-5 py-3 bg-zinc-900/40 min-w-[180px]"
              data-testid="usage-badge"
            >
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                {t("simulate.usage_today", { used: usage.used, limit: usage.limit }).split(":")[0]}
              </div>
              <div className="font-outfit text-3xl font-black text-white mt-1">
                {usage.used}
                <span className="text-zinc-600">/{usage.limit}</span>
              </div>
              <div className="text-xs text-orange-400 mt-1">
                {usage.remaining} {t("common.yes") === "Da" ? "disponibile" : "available"}
              </div>
            </div>
          )}
        </div>

        {/* Vision AI · Ghid Sales — chip link to the printable PDF brochure.
            Visible only to partner/sales/admin (same audience as the Vision
            AI opt-in banner). Served through /api/ to bypass ad-blockers
            that flag /downloads/*.pdf as trackers (ERR_BLOCKED_BY_CLIENT). */}
        {user && ["partner", "sales", "admin"].includes(user.role) && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/ghid-sales`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sales-guide-pdf-link"
            onClick={() => trackEvent("pdf.download.ghid_sales", { role: user.role })}
            className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg border border-orange-500/30 bg-orange-500/5 text-orange-300 hover:bg-orange-500/15 hover:border-orange-500/50 transition-colors text-sm font-semibold"
          >
            <BookOpen size={14} strokeWidth={1.5} />
            {t("common.yes") === "Da"
              ? "Ghid Sales · Vision AI (PDF)"
              : "Sales guide · Vision AI (PDF)"}
            <span className="text-[10px] uppercase tracking-wider text-orange-500/70">14 pag · 1.6MB</span>
          </a>
        )}

        {/* Sales CTA — visible to clients (simple_user + partner). Hidden
            for admin/sales staff who don't request quotes from themselves. */}
        {user && user.role !== "admin" && user.role !== "sales" && (
          <div className="mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-orange-500/20 bg-orange-500/5 rounded-lg p-4">
            <div className="text-sm text-zinc-300">
              <strong className="text-orange-300">
                {t("common.yes") === "Da" ? "Ai nevoie de o ofertă?" : "Need a quote?"}
              </strong>{" "}
              <span className="text-zinc-400">
                {t("common.yes") === "Da"
                  ? "Trimite-ne direct datele tale și simulările făcute. Echipa FDP te contactează."
                  : "Send us your details and your renders directly. The FDP team will get back to you."}
              </span>
            </div>
            <SalesQuoteButton testId="sales-quote-btn-dashboard" />
          </div>
        )}

        {/* Sprint 1 Vision AI — opt-in banner (partner/sales/admin only) */}
        <VisionAiOptInBanner testId="vision-optin-banner-selector" />

        <div className="grid md:grid-cols-2 gap-5">
          <Card
            to="/dashboard/interior"
            label={t("dashboard.card_interior_title").toUpperCase()}
            num="01"
            img={INTERIOR_IMG}
            icon={<HomeIcon size={20} strokeWidth={1.5} className="text-orange-500" />}
            description={t("dashboard.card_interior_subtitle") + ". " + t("dashboard.card_interior_caption") + "."}
            testId="select-interior-card"
          />
          <Card
            to="/dashboard/exterior"
            label={t("dashboard.card_exterior_title").toUpperCase()}
            num="02"
            img={EXTERIOR_IMG}
            icon={<Building2 size={20} strokeWidth={1.5} className="text-orange-500" />}
            description={t("dashboard.card_exterior_subtitle") + ". " + t("dashboard.card_exterior_caption") + "."}
            testId="select-exterior-card"
          />
        </div>

        <div className="mt-12 text-center text-sm text-zinc-500 max-w-2xl mx-auto">
          {t("common.yes") === "Da"
            ? <>Imaginile de interior <strong className="text-white">nu pot</strong> fi încărcate în dashboard-ul de exterior și invers — astfel AI-ul aplică întotdeauna profilele potrivite contextului.</>
            : <>Interior images <strong className="text-white">cannot</strong> be uploaded to the exterior dashboard and vice versa — this way the AI always applies the mouldings that fit the context.</>
          }
        </div>

        {/* User's existing simulations — visible for 72h (regular users) or
            permanently (sales / admin who can manually clean up). */}
        <div className="mt-16 border-t border-white/5 pt-12">
          <UserSimulationsList isStaff={user?.role === "admin" || user?.role === "sales"} />
        </div>

        <footer className="mt-16 border-t border-white/5 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-600">
          <div className="flex items-center gap-4">
            <Link
              to="/politica-confidentialitate"
              className="hover:text-orange-400 transition"
              data-testid="footer-privacy-link"
            >
              Politica de confidențialitate
            </Link>
            <span className="text-zinc-700">·</span>
            <span>Operator date: WEASEL ART SRL</span>
          </div>
          <DeleteAccountButton />
        </footer>
      </main>
    </div>
  );
}
